import { Badge, Button, cn, useArtifactCanvas } from '@yeshuman/sdk'
import { ClipboardCheck, RefreshCw, Sparkles, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  type KitQuestion,
  useApplicationInterviewKit,
  useBriefInterviewKit,
  useDeleteKitQuestion,
  useGenerateInterviewKit,
} from '../../hooks/use-interview-kits'
import { ScorecardSummaryLine } from './scorecard-summary'
import { ScorecardCanvasForm } from './scorecard-canvas-form'

export type InterviewKitPanelProps = {
  briefId: string
  /** When set, the panel shows this candidate's scorecards and lets the interviewer score. */
  applicationId?: string | null
  candidateName?: string
  interviewId?: string | null
  className?: string
}

function groupByTopic(questions: KitQuestion[]) {
  const groups: { topic: string; targetLevel: number | null; questions: KitQuestion[] }[] = []
  for (const q of questions) {
    const last = groups[groups.length - 1]
    if (last && last.topic === q.topic) last.questions.push(q)
    else groups.push({ topic: q.topic, targetLevel: q.target_level, questions: [q] })
  }
  return groups
}

/** The brief's interview questions, by competency (or skill), with scoring for one candidate. */
export function InterviewKitPanel({
  briefId,
  applicationId,
  candidateName = 'the candidate',
  interviewId,
  className,
}: InterviewKitPanelProps) {
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const kitQuery = useBriefInterviewKit(briefId)
  const appQuery = useApplicationInterviewKit(applicationId)
  const generate = useGenerateInterviewKit()
  const removeQuestion = useDeleteKitQuestion()
  const kit = kitQuery.data ?? null
  const scorecards = appQuery.data?.scorecards ?? []

  const onGenerate = () =>
    generate.mutate(briefId, {
      onSuccess: (k) => toast.success(`Drafted ${k.questions.length} questions`),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Could not draft questions'),
    })

  const onScore = () => {
    if (!kit || !applicationId) return
    openCanvas({
      title: `Scorecard · ${candidateName}`,
      content: (
        <ScorecardCanvasForm
          applicationId={applicationId}
          candidateName={candidateName}
          kit={kit}
          interviewId={interviewId}
          onClose={closeCanvas}
        />
      ),
    })
  }

  return (
    <section className={cn('space-y-3', className)} aria-label="Interview kit">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-medium">Interview kit</h2>
          <p className="text-xs text-muted-foreground">
            {kit
              ? kit.role_title
                ? `Questions for ${kit.role_title}, pitched at each target level.`
                : "Questions from the brief's skills."
              : 'No questions yet. Draft them from the role, or from the skills when there is no role.'}
          </p>
        </div>
        <div className="flex gap-2">
          {applicationId && kit ? (
            <Button type="button" size="sm" onClick={onScore}>
              <ClipboardCheck className="size-3.5" aria-hidden />
              Score candidate
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onGenerate}
            disabled={generate.isPending}
          >
            {kit ? (
              <RefreshCw className="size-3.5" aria-hidden />
            ) : (
              <Sparkles className="size-3.5" aria-hidden />
            )}
            {generate.isPending ? 'Drafting…' : kit ? 'Redraft' : 'Draft questions'}
          </Button>
        </div>
      </div>

      {scorecards.length > 0 ? (
        <ul className="space-y-1">
          {scorecards.map((card) => (
            <li key={card.id}>
              <ScorecardSummaryLine
                recommendation={card.recommendation}
                recommendationLabel={card.recommendation_label}
                interviewerName={card.interviewer_name}
                ratings={card.ratings}
              />
            </li>
          ))}
        </ul>
      ) : null}

      {kitQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading kit…</p>
      ) : kitQuery.error ? (
        <p className="text-sm text-destructive">Unable to load the interview kit.</p>
      ) : kit ? (
        <div className="space-y-3">
          {groupByTopic(kit.questions).map((group) => (
            <div key={group.topic} className="space-y-1.5">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium">{group.topic}</h3>
                {group.targetLevel != null ? (
                  <Badge variant="secondary">target {group.targetLevel}</Badge>
                ) : null}
              </div>
              <ol className="space-y-1.5">
                {group.questions.map((q) => (
                  <li key={q.id} className="rounded-md border border-border/60 p-2">
                    <details className="group">
                      <summary className="flex cursor-pointer list-none items-start justify-between gap-2 text-sm">
                        <span>{q.question}</span>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghostMuted"
                          className="size-6 shrink-0"
                          aria-label="Remove question"
                          disabled={removeQuestion.isPending}
                          onClick={(e) => {
                            e.preventDefault()
                            removeQuestion.mutate(q.id)
                          }}
                        >
                          <Trash2 className="size-3.5" aria-hidden />
                        </Button>
                      </summary>
                      <div className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                        {q.probes.length > 0 ? (
                          <ul className="list-disc space-y-0.5 pl-4">
                            {q.probes.map((p) => (
                              <li key={p}>{p}</li>
                            ))}
                          </ul>
                        ) : null}
                        {q.strong_answer ? (
                          <p>
                            <span className="font-medium text-foreground">Strong: </span>
                            {q.strong_answer}
                          </p>
                        ) : null}
                        {q.weak_answer ? (
                          <p>
                            <span className="font-medium text-foreground">Weak: </span>
                            {q.weak_answer}
                          </p>
                        ) : null}
                      </div>
                    </details>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  )
}
